import { useCallback, useRef, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Textarea } from '../atoms/Textarea.js';
import { Button } from '../atoms/Button.js';

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  maxLength?: number;
}

export function ChatInput({
  value,
  onChange,
  onSubmit,
  disabled = false,
  loading = false,
  placeholder,
  maxLength = 2000,
}: ChatInputProps) {
  const { t } = useTranslation('interview');
  const resolvedPlaceholder = placeholder ?? t('chat_input.placeholder');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // IME変換中のEnterは無視（日本語入力対応）
      if (e.nativeEvent.isComposing || e.keyCode === 229) return;

      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (value.trim() && !disabled && !loading) {
          onSubmit();
        }
      }
    },
    [value, disabled, loading, onSubmit],
  );

  return (
    <div className="flex items-end gap-2">
      <div className="flex-1">
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={resolvedPlaceholder}
          maxLength={maxLength}
          showCount
          autoResize
          disabled={disabled || loading}
          className="min-h-[44px] max-h-[160px]"
        />
      </div>
      <Button
        onClick={onSubmit}
        disabled={!value.trim() || disabled}
        loading={loading}
        size="md"
        className="shrink-0"
        aria-label={t('chat_input.send')}
      >
        <svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 19V5m0 0l-7 7m7-7l7 7" />
        </svg>
      </Button>
    </div>
  );
}
